import React, { ChangeEvent, useEffect } from "react";

import Input from "../../../Input";
import MaskedInput from "../../../MaskedInput";

import { useSubClientRegister } from "contexts/register/SubClientContext";
import { IClient } from "interfaces/Client";
import { ISuperficialClient } from "interfaces/SubClient";

import * as S from "./styles";
import { InputMap } from "components/InputMap";
import { AutoComplete } from "components/Autocomplete";
import { useDataList } from "hooks/useDataList";

type props = {
  subClientData?: ISuperficialClient;
};

export function SuperficialClientForm({ subClientData }: props) {
  const {
    subClient,
    setSubClient,
    errors,
    addError,
    removeError,
    setAddress,
    setCoordX,
    setCoordY,
    setEstablishmentName,
  } = useSubClientRegister();

  const { data } = useDataList<IClient>({
    queryKey: 'listAllClients',
    route: 'clients',
    filter: {
      notPage: true,
    }
  })

  function validateCNPJ(event: React.ChangeEvent<HTMLInputElement>) {
    const cnpj = event.target.value.replace("_", "");
    if (cnpj.length < 18 && cnpj.length > 5) {
      addError("cnpj", "CNPJ inválido");
      return;
    }

    removeError("cnpj");
  }

  function formatAddress() {
    const address = subClient?.superficialAddress;
    if (!address || !address.coordX || !address.coordY) return undefined;

    return {
      lat: address.coordX,
      lng: address.coordY,
      formattedAddress: `${address.street ?? "--"}, ${address.number ?? "--"
        }, ${address.city ?? "--"}, ${address.uf ?? "--"}`,
    };
  }

  useEffect(() => {
    console.log({ errors })
  }, [errors])

  return (
    <S.Wrapper>
      <S.Form>
        <Input
          defaultValue={subClient.name}
          id="name"
          name="name"
          onChange={(event) => {
            setSubClient((_value) => {
              return {
                ..._value,
                name: event.target.value,
              };
            });
          }}
          label="Nome completo"
          required={true}
          type="text"
          error={errors["client.name"]}
          apiErrorName={"client.name"}
          handleApiErrorRemove={() => removeError("client.name")}
        />

        <MaskedInput
          defaultValue={subClient.superficialAddress?.cnpj}
          onBlur={validateCNPJ}
          id="CNPJ"
          name="CNPJ"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setAddress((_value) => ({
              ..._value,
              cnpj: event.target.value,
            }));
          }}
          label="CNPJ do estabelecimento (opcional)"
          required={true}
          type="text"
          mask="99.999.999/9999-99"
          error={
            errors.cnpj ||
            errors["client.cnpj"] ||
            errors["superficial_clients.cnpj"]
          }
          apiErrorName={"client.cnpj"}
          handleApiErrorRemove={() => removeError("client.cnpj")}
        />

        {data && (
          <AutoComplete
            label="Selecione um cliente"
            id="client"
            defaultValue={subClientData?.client}
            isSearchable
            isMulti={false}
            options={data.items}
            getOptionLabel={(option) => option.name}
            noOptionsMessage={() => 'Nenhum cliente encontrado'}
            placeholder="Selecione um cliente"
            onChange={(item) => {
              if (!item) {
                setSubClient((_value) => ({
                  ..._value,
                  clientId: ''
                }));
              }

              if (item) {
                setSubClient((_value) => ({
                  ..._value,
                  clientId: (item as IClient).id
                }));
              }
            }}
            onBlur={() => {
              if (!subClient.clientId) {
                addError("noClientSelected", "Necessario selecionar um cliente!")
              } else {
                removeError("noClientSelected")
              }
            }}
            error={errors["client.client.id"] || errors["noClientSelected"]}
            apiErrorName={"client.client.id"}
            handleApiErrorRemove={() => removeError("client.client.id")}
          />
        )}

        <S.Map>
          <Input
            defaultValue={
              subClient
                ? subClient.superficialAddress?.name
                : subClientData?.superficialAddress.name
            }
            id="establishmentName"
            name="establishmentName"
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setEstablishmentName(event.target.value);
              setSubClient((_value) => {
                if (_value.superficialAddress?.name) {
                  _value.superficialAddress.name = event.target.value;
                } else {
                  _value.superficialAddress = {
                    ..._value.superficialAddress,
                    name: event.target.value,
                  };
                }
                return _value;
              });
            }}
            label="Nome do estabelecimento"
            required={true}
            type="text"
            apiErrorName={"address.name"}
            handleApiErrorRemove={() => removeError("address.name")}
          />

          <InputMap
            label="Endereço do estabelecimento"
            location={formatAddress()}
            onCoordsChange={(coords) => {
              removeError("addressMap");
              setCoordX(coords.lat);
              setCoordY(coords.lng);
            }}
            onAddressChange={(_address) => {
              setAddress((previousAddress) => ({
                ...previousAddress,
                ..._address,
              }));
            }}
            error={errors["addressMap"]}
          />
          {errors["addressMap"] && (
            <S.MapError>{errors["addressMap"]}</S.MapError>
          )}
        </S.Map>
      </S.Form>
    </S.Wrapper>
  );
}
